/**
 * Net Profit (monthly) bar chart for the Profit & Loss page (dashboards
 * round 3, commit 6): Jan-Dec of the current year, shared by the entity
 * scope, personal scope, and the All Entities / All Personal aggregates.
 * See lib/pnlNetProfitChart.js for the data model and the actuals/forecast
 * rules.
 *
 * Negative months render as bars below the zero line -- no Math.abs, ever;
 * a ReferenceLine at y=0 makes the sign unambiguous even when every visible
 * bar happens to be positive.
 */
import {
  ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, LabelList, ReferenceLine, Line,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCompact, formatCurrencyFull } from '../../lib/chartFormatters';
import { ForecastCaveat } from '../../lib/forecastChartUtils';

export function PnlNetProfitChart({ chartData, displayCurrency }) {
  if (!chartData || chartData.length === 0) return null;
  var hasAnyData = chartData.some(function (r) { return r.net != null || r.net_forecast != null; });
  if (!hasAnyData) return null;
  var hasForecast = chartData.some(function (r) { return r._isForecast; });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Net Profit (monthly)</CardTitle>
        <ForecastCaveat show={hasForecast} />
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={340}>
          <ComposedChart data={chartData} margin={{ top: 24, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => formatCompact(v, displayCurrency)} tick={{ fontSize: 11 }} width={60} />
            <Tooltip
              formatter={(val, name) => [val == null ? 'No data' : formatCurrencyFull(val, displayCurrency), name]}
              contentStyle={{ fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine y={0} stroke="hsl(var(--foreground))" strokeOpacity={0.4} />
            <Bar dataKey="net" name="Net Profit" stackId="netProfit" fill="#0EA5E9">
              <LabelList
                position="top"
                formatter={(v) => (v == null || v === 0 ? '' : formatCompact(v, displayCurrency))}
                fontSize={11}
                fontWeight={700}
                fill="hsl(var(--foreground))"
              />
            </Bar>
            <Bar dataKey="net_forecast" name="Net Profit (forecast)" stackId="netProfit"
              fill="#0EA5E9" fillOpacity={0.35} stroke="#0EA5E9" strokeDasharray="4 2" strokeWidth={1}>
              <LabelList
                position="top"
                formatter={(v) => (v == null || v === 0 ? '' : formatCompact(v, displayCurrency))}
                fontSize={11}
                fontWeight={700}
                fill="#0EA5E9"
              />
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function BySourceTooltip({ active, payload, label, segments, displayCurrency }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-md text-sm min-w-[220px]">
      <p className="font-semibold mb-2">{label}</p>
      {segments.map((seg) => {
        const actual = point[seg.label];
        const forecast = point[`${seg.label}_forecast`];
        return (
          <div key={seg.key} className="flex justify-between gap-6">
            <span style={{ color: seg.color }}>{seg.label}{forecast != null ? ' (incl. forecast)' : ''}</span>
            <span className="font-mono">
              {actual == null && forecast == null ? 'No data' : formatCurrencyFull((actual || 0) + (forecast || 0), displayCurrency)}
            </span>
          </div>
        );
      })}
      <div className="flex justify-between gap-6 border-t pt-1 mt-2 font-medium">
        <span>Net Profit</span>
        <span className="font-mono">{point._total == null ? 'No data' : formatCurrencyFull(point._total, displayCurrency)}</span>
      </div>
    </div>
  );
}

/**
 * In-bar value label for one stacked segment. Drawn only when the bar is
 * tall enough to hold the text, so thin slices never overlap.
 */
function segmentValueLabel(fill, displayCurrency) {
  return function SegmentValueLabel(lp) {
    const { x, y, width, height, value } = lp;
    if (value == null || value === 0) return null;
    const h = Math.abs(height || 0);
    if (h < 14 || (width || 0) < 22) return null;
    const top = Math.min(y, y + (height || 0));
    return (
      <text x={x + width / 2} y={top + h / 2} textAnchor="middle" dominantBaseline="central"
        fontSize={10} fontWeight={600} fill={fill}>
        {formatCompact(value, displayCurrency)}
      </text>
    );
  };
}

/**
 * Per-segment totals under the chart (same idea as the dashboard Margin
 * chart footer): actual to date, and full year including forecast.
 */
function SegmentTotalsFooter({ data, segments, displayCurrency }) {
  const sum = (key) => data.reduce((s, r) => s + (r[key] || 0), 0);
  const rows = [
    { title: 'Actual to date', pick: (seg) => sum(seg.label) },
    { title: 'Full year incl. forecast', pick: (seg) => sum(seg.label) + sum(`${seg.label}_forecast`) },
  ];
  return (
    <div className="mt-2 space-y-1">
      {rows.map((row) => {
        const vals = segments.map((seg) => row.pick(seg));
        const total = vals.reduce((a, b) => a + b, 0);
        return (
          <div key={row.title} className="text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
            <span>{row.title}:</span>
            {segments.map((seg, i) => (
              <span key={seg.key} className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: seg.color }} />
                <span>{seg.label}</span>
                <span className="font-mono font-semibold text-foreground">{formatCompact(vals[i], displayCurrency)}</span>
              </span>
            ))}
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-foreground" />
              <span>Combined</span>
              <span className="font-mono font-semibold text-foreground">{formatCompact(total, displayCurrency)}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Net Profit (monthly) stacked by source -- BR OS and Property Portfolio,
 * same colours and actual/forecast styling as the dashboard MarginChart.
 * Data: lib/pnlNetProfitChart.buildNetProfitBySourceChartData. The month
 * total sits on a zero-height anchor bar stacked last (so at the net sum).
 */
export function PnlNetProfitBySourceChart({ chartData, segments, displayCurrency }) {
  if (!chartData || chartData.length === 0 || !segments || segments.length === 0) return null;
  const hasAnyData = chartData.some((r) => r._total != null);
  if (!hasAnyData) return null;
  const hasForecast = chartData.some((r) => r._isForecast);
  // Month total sits on top of the positive stack. A zero-height stacked
  // anchor bar lands at y=0 once any month has a negative segment (recharts
  // sign stacking), so the label rides an unstacked invisible Line instead.
  const keys = segments.flatMap((s) => [s.label, `${s.label}_forecast`]);
  const data = chartData.map((r) => ({
    ...r,
    _labelY: r._total == null ? null
      : keys.reduce((a, k) => a + (Number(r[k]) > 0 ? Number(r[k]) : 0), 0),
  }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Net Profit (monthly)</CardTitle>
        <ForecastCaveat show={hasForecast} />
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={340}>
          <ComposedChart data={data} margin={{ top: 24, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => formatCompact(v, displayCurrency)} tick={{ fontSize: 11 }} width={60} />
            <Tooltip content={<BySourceTooltip segments={segments} displayCurrency={displayCurrency} />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine y={0} stroke="hsl(var(--foreground))" strokeOpacity={0.4} />
            {segments.map((seg) => (
              <Bar key={seg.key} dataKey={seg.label} name={seg.label} stackId="netProfit" fill={seg.color}>
                {/* Each segment carries its own value, like the dashboard
                    Margin chart (Fan, 2026-10-27: "both numbers"). */}
                <LabelList dataKey={seg.label} content={segmentValueLabel('#fff', displayCurrency)} />
              </Bar>
            ))}
            {segments.map((seg) => (
              data.some((d) => d[`${seg.label}_forecast`] != null) ? (
                <Bar
                  key={`${seg.key}_forecast`}
                  dataKey={`${seg.label}_forecast`}
                  name={`${seg.label} (forecast)`}
                  stackId="netProfit"
                  fill={seg.color}
                  fillOpacity={0.35}
                  stroke={seg.color}
                  strokeDasharray="4 2"
                  strokeWidth={1}
                >
                  <LabelList dataKey={`${seg.label}_forecast`} content={segmentValueLabel(seg.color, displayCurrency)} />
                </Bar>
              ) : null
            ))}
            <Line dataKey="_labelY" stroke="none" dot={false} activeDot={false} legendType="none"
              isAnimationActive={false} tooltipType="none">
              <LabelList
                content={(lp) => {
                  const { x, y, index } = lp;
                  const row = data[index];
                  if (!row || row._total == null || row._total === 0) return null;
                  return (
                    <text x={x} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={700}
                      fill="hsl(var(--foreground))">
                      {formatCompact(row._total, displayCurrency)}
                    </text>
                  );
                }}
              />
            </Line>
          </ComposedChart>
        </ResponsiveContainer>
        <SegmentTotalsFooter data={data} segments={segments} displayCurrency={displayCurrency} />
      </CardContent>
    </Card>
  );
}
